import { api } from './api.js';
import {
  STATUS_CFG, STATUS_LABELS, STATUS_BUCKETS, esc, getInitials, getAvatarColor,
  fmtDate, fmtAmt, isOverdue, relativeTime, showToast, hasPerm, isViewer,
  canViewOwner, applyNavAccess,   leadOutstandingBalance, getStatusesForType,
  ticketQuotedTotal, isBookingExpired,
} from './utils.js';
import { renderFilterChips, syncScrollStrip } from './ux.js';
import { icon } from './icons.js';
import { initPageChrome } from './page-chrome.js';
import { toggleTheme } from './theme-ui.js';
import { initDensityControls } from './density-ui.js';
import { installSessionHandler, clearSessionHint } from './session.js';
import {
  initDetailHost, openDetail, syncDetailHostLeads, bindDetailHost, loadAccessInbox,
} from './app.js';
import { applyPipelineSettings } from './status-pipelines.js';

const EXPLORER_LIMIT = 25;
const DEPARTMENTS = ['tour', 'visa', 'ticket'];
const DEPARTMENT_LABELS = { tour: 'Tours', visa: 'Visa', ticket: 'Tickets' };
const PIPE_COLORS = {
  active: '#00AEEF',
  waiting_client: '#f59e0b',
  waiting_supplier: '#8A63D2',
  completed: '#3ddeb0',
  closed: '#94a3b8',
};

const DEFAULT_TAB = 'browse';

const state = {
  agent: null,
  agents: [],
  settings: { sources: [] },
  stats: null,
  tab: DEFAULT_TAB,
  assistantEmbed: false,
  poolCount: 0,
  poolStaleCount: 0,
  filter: {
    type: '', status: '', bucket: '', agent_id: '', agent_q: '', source: '',
    q: '', overdue: false, custom: false, booking_expired: false, focus: false, unassigned: false, dept_scope: 'my',
    search_scope: '',
  },
  sort: 'updated',
  page: 1,
  total: 0,
  total_pages: 1,
  leads: [],
  listFetchGen: 0,
};

const $ = (id) => document.getElementById(id);

async function loadDetailShell() {
  if (document.getElementById('detailPanel')) return;
  const [shell, modals] = await Promise.all([
    fetch('/includes/detail-shell.html').then((r) => {
      if (!r.ok) throw new Error('Could not load detail panel');
      return r.text();
    }),
    fetch('/includes/detail-modals.html').then((r) => {
      if (!r.ok) throw new Error('Could not load detail modals');
      return r.text();
    }),
  ]);
  document.body.insertAdjacentHTML('beforeend', shell + modals);
}

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function effectiveDeptScope() {
  if (!state.agent || state.agent.role === 'admin') return 'all';
  if (state.filter.dept_scope === 'all') return 'all';
  if (state.filter.type) return 'all';
  return 'my';
}

function showDepartmentScope() {
  return state.agent
    && state.agent.role !== 'admin'
    && state.agent.departments?.length
    && state.agent.departments.length < DEPARTMENTS.length;
}

function syncDeptScopeUI() {
  const show = showDepartmentScope();
  $('deptScopeMy')?.classList.toggle('hidden', !show);
  $('deptScopeAll')?.classList.toggle('hidden', !show);
  if (!show) return;
  $('deptScopeMy')?.classList.toggle('active', state.filter.dept_scope !== 'all');
  $('deptScopeAll')?.classList.toggle('active', state.filter.dept_scope === 'all');
}

function readUrlParams() {
  const p = new URLSearchParams(location.search);
  const rawTab = p.get('tab');
  let tab = rawTab || DEFAULT_TAB;

  // Legacy URLs used ?focus=1 without tab — land on Browse all, not Focus.
  if (!rawTab && p.get('focus') === '1') {
    tab = DEFAULT_TAB;
    p.delete('focus');
    const qs = p.toString();
    history.replaceState(null, '', qs ? `${location.pathname}?${qs}` : location.pathname);
  }

  state.tab = tab;
  state.page = Math.max(1, Number(p.get('page') || 1));
  state.sort = p.get('sort') || 'updated';
  state.assistantEmbed = p.get('assistant') === '1';
  state.filter = {
    type: p.get('type') || '',
    status: p.get('status') || '',
    bucket: p.get('bucket') || '',
    agent_id: p.get('agent_id') || '',
    agent_q: p.get('agent_q') || '',
    source: p.get('source') || '',
    q: p.get('q') || '',
    overdue: p.get('overdue') === '1',
    custom: p.get('custom') === '1',
    booking_expired: p.get('booking_expired') === '1',
    focus: false,
    unassigned: p.get('unassigned') === '1',
    dept_scope: p.get('dept_scope') === 'all' ? 'all' : 'my',
    search_scope: p.get('search_scope') === 'global' ? 'global' : '',
  };
  if (tab === 'focus') {
    state.filter.focus = p.get('focus') !== '0';
  }
}

function writeUrlParams() {
  const p = new URLSearchParams();
  if (state.tab !== DEFAULT_TAB) p.set('tab', state.tab);
  if (state.page > 1) p.set('page', String(state.page));
  if (state.sort !== 'updated') p.set('sort', state.sort);
  if (state.filter.type) p.set('type', state.filter.type);
  if (state.filter.status) p.set('status', state.filter.status);
  if (state.filter.bucket) p.set('bucket', state.filter.bucket);
  if (state.filter.unassigned) p.set('unassigned', '1');
  if (state.filter.agent_id) p.set('agent_id', state.filter.agent_id);
  if (state.filter.agent_q) p.set('agent_q', state.filter.agent_q);
  if (state.filter.source) p.set('source', state.filter.source);
  if (state.filter.q) p.set('q', state.filter.q);
  if (state.filter.search_scope === 'global') p.set('search_scope', 'global');
  if (state.filter.overdue) p.set('overdue', '1');
  if (state.filter.custom) p.set('custom', '1');
  if (state.filter.booking_expired) p.set('booking_expired', '1');
  if (state.tab === 'focus' && state.filter.focus) p.set('focus', '1');
  else if (state.tab === 'focus') p.set('focus', '0');
  if (state.assistantEmbed) p.set('assistant', '1');
  if (effectiveDeptScope() === 'all') p.set('dept_scope', 'all');
  const qs = p.toString();
  history.replaceState(null, '', qs ? `${location.pathname}?${qs}` : location.pathname);
}

function syncToolbarFromFilter() {
  if ($('leadsSearch')) $('leadsSearch').value = state.filter.q || '';
  if ($('fStatus')) $('fStatus').value = state.filter.status || '';
  if ($('fSource')) $('fSource').value = state.filter.source || '';
  if ($('fSort')) $('fSort').value = state.sort;
  $('fOverdue')?.classList.toggle('active', state.filter.overdue);
  $('fOverdue')?.setAttribute('aria-pressed', state.filter.overdue ? 'true' : 'false');
  $('fCustom')?.classList.toggle('active', state.filter.custom);
  $('fCustom')?.setAttribute('aria-pressed', state.filter.custom ? 'true' : 'false');
  $('fBookingExpired')?.classList.toggle('active', state.filter.booking_expired);
  $('fBookingExpired')?.setAttribute('aria-pressed', state.filter.booking_expired ? 'true' : 'false');
  syncFilterPanels();
}

function canPickAgentFilter() {
  return state.agent && (state.agent.role === 'admin' || hasPerm(state.agent, 'view_all') || hasPerm(state.agent, 'edit_all'));
}

function visibleAgentsForFilter() {
  if (canPickAgentFilter()) return state.agents.filter((a) => a.active !== false);
  return state.agents.filter((a) => a.id === state.agent?.id);
}

function syncFilterPanels() {
  document.querySelectorAll('#typeGrid [data-type-filter]').forEach((btn) => {
    const t = btn.dataset.typeFilter === 'all' ? '' : (btn.dataset.typeFilter || '');
    btn.classList.toggle('active', (state.filter.type || '') === t);
  });
  document.querySelectorAll('#agentGrid [data-agent-id]').forEach((btn) => {
    const id = btn.dataset.agentId || '';
    btn.classList.toggle('active', !state.filter.unassigned && (state.filter.agent_id || '') === id);
  });
  document.querySelectorAll('#agentGrid [data-unassigned-pool]').forEach((btn) => {
    btn.classList.toggle('active', !!state.filter.unassigned);
  });
  syncDepartmentTabIndicator();
}

function syncDepartmentTabIndicator() {
  const typeTab = document.querySelector('[data-leads-tab="type"]');
  if (!typeTab) return;
  typeTab.classList.remove('tab-has-filter', 'tab-filter-tour', 'tab-filter-visa', 'tab-filter-ticket');
  if (state.filter.type) {
    typeTab.classList.add('tab-has-filter', `tab-filter-${state.filter.type}`);
    typeTab.setAttribute('aria-current', 'true');
  } else {
    typeTab.removeAttribute('aria-current');
  }
}

function setTypeFilter(type) {
  const next = state.filter.type === type && type ? '' : type;
  state.filter.type = next;
  state.filter.focus = false;
  populateStatusSelect();
  if (state.filter.type && state.filter.status) {
    const allowed = getStatusesForType(state.filter.type);
    if (!allowed.includes(state.filter.status)) state.filter.status = '';
  }
  renderTypePanel();
  onFilterChange();
}

function setAgentFilter(agentId) {
  const next = state.filter.agent_id === agentId && agentId ? '' : agentId;
  state.filter.agent_id = next;
  state.filter.focus = false;
  onFilterChange();
}

function setUnassignedFilter(on) {
  state.filter.unassigned = !!on;
  if (state.filter.unassigned) {
    state.filter.agent_id = '';
    state.filter.type = state.filter.type || 'ticket';
    state.filter.status = 'new';
  }
  state.filter.focus = false;
  onFilterChange();
}

function buildListParams() {
  const params = new URLSearchParams({
    page: String(state.page),
    limit: String(EXPLORER_LIMIT),
    sort: state.sort,
  });
  const f = state.filter;
  if (f.type) params.set('type', f.type);
  if (f.status) params.set('status', f.status);
  if (f.bucket) params.set('bucket', f.bucket);
  if (f.unassigned) params.set('unassigned', '1');
  if (f.agent_id) params.set('agent_id', f.agent_id);
  if (f.agent_q) params.set('agent_q', f.agent_q);
  if (f.source) params.set('source', f.source);
  if (f.q) params.set('q', f.q);
  if (f.search_scope === 'global') params.set('search_scope', 'global');
  if (f.overdue) params.set('overdue', '1');
  if (f.custom) params.set('custom', '1');
  if (f.booking_expired) params.set('booking_expired', '1');
  if (f.focus && state.tab === 'focus') params.set('focus', '1');
  if (!f.agent_id && !f.unassigned && state.tab === 'browse' && !f.q) {
    params.set('pool_first', '1');
  }
  params.set('dept_scope', effectiveDeptScope());
  return params;
}

function setListLoading(on) {
  $('tablePanel')?.classList.toggle('is-loading', on);
  $('tableLoading')?.classList.toggle('hidden', !on);
}

function onFilterChange() {
  state.page = 1;
  populateStatusSelect();
  writeUrlParams();
  syncToolbarFromFilter();
  renderFilterChips(state.filter, state.agents, onFilterChange);
  refreshList();
}

function clearFilters() {
  state.filter = {
    type: '', status: '', bucket: '', agent_id: '', agent_q: '', source: '',
    q: '', overdue: false, custom: false, booking_expired: false, unassigned: false,
    focus: state.tab === 'focus',
    dept_scope: state.filter.dept_scope || 'my',
    search_scope: '',
  };
  state.sort = 'updated';
  state.page = 1;
  onFilterChange();
}

function applyStatFilter(key) {
  const deptScope = state.filter.dept_scope || 'my';
  const q = state.filter.q;
  state.filter = {
    type: '', status: '', bucket: '', agent_id: '', agent_q: '', source: '',
    q, overdue: false, custom: false, booking_expired: false, focus: state.tab === 'focus',
    dept_scope: deptScope,
    search_scope: state.filter.search_scope || '',
  };
  if (key === 'overdue') {
    state.filter.overdue = true;
    state.filter.focus = false;
  } else if (key === 'booking_expired') {
    state.filter.booking_expired = true;
    state.filter.overdue = false;
    state.filter.focus = false;
    state.filter.type = 'ticket';
    state.filter.status = 'temporary_booking';
  } else if (key.startsWith('bucket:')) {
    state.filter.bucket = key.slice(7);
    state.filter.focus = false;
  } else if (key === 'tour' || key === 'visa' || key === 'ticket') {
    state.filter.type = key;
    state.filter.focus = false;
    if (state.tab !== 'type') setActiveTab('type', false);
  }
  populateStatusSelect();
  onFilterChange();
}

function applyTabUi() {
  document.querySelectorAll('[data-leads-tab]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.leadsTab === state.tab);
  });
  syncScrollStrip($('leadsTabs'));
  document.querySelectorAll('.leads-insight-panel').forEach((el) => el.classList.add('hidden'));
  const panelMap = {
    focus: 'insightFocus',
    overview: 'insightOverview',
    progress: 'insightProgress',
    type: 'insightType',
    agents: 'insightAgents',
  };
  const panelId = panelMap[state.tab];
  if (panelId) $(panelId)?.classList.remove('hidden');
}

function setActiveTab(tab, reload = true) {
  state.tab = tab;
  state.filter.focus = tab === 'focus';
  applyTabUi();
  writeUrlParams();
  syncFilterPanels();
  if (tab === 'type') renderTypePanel();
  if (reload) refreshList();
}

function populateStatusSelect() {
  const sel = $('fStatus');
  if (!sel) return;
  const statuses = state.filter.type ? getStatusesForType(state.filter.type) : Object.keys(STATUS_LABELS);
  sel.innerHTML = '<option value="">All stages</option>'
    + statuses.map((s) => `<option value="${esc(s)}">${esc(STATUS_LABELS[s] || s)}</option>`).join('');
  if (state.filter.status) sel.value = state.filter.status;
}

function renderTypePanel() {
  const grid = $('typeGrid');
  if (!grid) return;
  const types = state.stats?.by_type || {};
  const total = state.stats?.total;
  const cards = [
    { key: 'all', label: 'All types', count: total, cls: 'type-all' },
    { key: 'tour', label: 'Tours', count: types.tour, cls: 'type-tour' },
    { key: 'visa', label: 'Visa', count: types.visa, cls: 'type-visa' },
    { key: 'ticket', label: 'Tickets', count: types.ticket, cls: 'type-ticket' },
  ];
  const TYPE_DOT = { tour: '#00AEEF', visa: '#4FD6E8', ticket: '#6BB6FF' };
  grid.innerHTML = cards.map((c) => {
    const filterKey = c.key === 'all' ? '' : c.key;
    const active = (state.filter.type || '') === filterKey;
    const countHtml = c.count != null ? `<strong>${c.count}</strong>` : '';
    const dotHtml = c.key !== 'all'
      ? `<span class="agent-dot" style="background:${TYPE_DOT[c.key] || '#888'}"></span>`
      : '';
    return `<button type="button" class="leads-filter-card ${c.cls}${active ? ' active' : ''}" data-type-filter="${esc(c.key)}">
      ${dotHtml}<span class="leads-filter-card-label">${esc(c.label)}</span>${countHtml}
    </button>`;
  }).join('');
}

function populateSourceSelect() {
  const sel = $('fSource');
  if (!sel) return;
  const sources = state.settings.sources || [];
  sel.innerHTML = '<option value="">All sources</option>'
    + sources.map((s) => `<option value="${esc(s)}">${esc(s)}</option>`).join('');
  if (state.filter.source) sel.value = state.filter.source;
}

function formatTicketRoute(l) {
  if (l.trip_type === 'multi' && l.extra?.flight_segments?.length) {
    let route = l.extra.flight_segments[0]?.origin || '—';
    l.extra.flight_segments.forEach((s) => { route += `→${s.destination || '?'}`; });
    return `${route} · multi`;
  }
  return `${l.origin || '—'} → ${l.destination || '—'}`;
}

function notifyAssistantOpenLead(leadId) {
  if (!state.assistantEmbed || !leadId) return;
  window.parent.postMessage({ type: 'mysky-assistant-open-lead', leadId }, window.location.origin);
}

function applyAssistantEmbedUi() {
  if (!state.assistantEmbed) return;
  document.documentElement.classList.add('leads-assistant-embed');
  document.querySelector('.site-footer')?.classList.add('hidden');
  document.querySelector('#assistantExit')?.classList.add('hidden');
  document.querySelector('#trackerLink')?.classList.add('hidden');
  document.querySelector('#newLeadBtn')?.classList.add('hidden');

  const main = document.querySelector('.leads-explorer-main');
  if (main && !$('assistantEmbedBanner')) {
    const banner = document.createElement('div');
    banner.id = 'assistantEmbedBanner';
    banner.className = 'leads-assistant-banner glass';
    banner.textContent = 'Full leads browser — filter below, then Open in terminal on any row.';
    main.prepend(banner);
  }

  const theadRow = document.querySelector('.leads-table thead tr');
  if (theadRow && !theadRow.querySelector('.leads-terminal-col')) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.className = 'leads-terminal-col';
    th.textContent = 'Assistant';
    theadRow.appendChild(th);
  }
}

function isPoolLead(l) {
  return l.type === 'ticket' && l.status === 'new' && l.agent_id == null;
}

function isStalePoolLead(l) {
  if (!isPoolLead(l)) return false;
  const ref = l.inquiry_date || l.created_at;
  if (!ref) return false;
  const ageMs = Date.now() - new Date(ref).getTime();
  return ageMs > 4 * 60 * 60 * 1000;
}

function renderPoolBanner() {
  const banner = $('leadsPoolBanner');
  if (!banner) return;
  const count = state.poolCount || 0;
  const show = count > 0 && !state.filter.unassigned && state.tab === 'browse';
  banner.classList.toggle('hidden', !show);
  if (!show) return;
  const stale = state.poolStaleCount || 0;
  if ($('leadsPoolBannerTitle')) {
    $('leadsPoolBannerTitle').textContent = `${count} unassigned ticket${count === 1 ? '' : 's'} in pool`;
  }
  if ($('leadsPoolBannerSub')) {
    $('leadsPoolBannerSub').textContent = stale > 0
      ? `${stale} waiting over 4 hours — claim and quote soon.`
      : 'Shown first in the list below. Claim one to start quoting.';
  }
}

async function refreshPoolCount() {
  try {
    const list = await api('/api/leads?unassigned=1&type=ticket&limit=50&sort=updated');
    state.poolCount = list.total || 0;
    state.poolStaleCount = (list.leads || []).filter(isStalePoolLead).length;
    renderPoolBanner();
  } catch {
    /* ignore */
  }
}

function renderTable() {
  const tbody = $('leadsBody');
  const empty = $('emptyState');
  if (!state.leads.length) {
    tbody.innerHTML = '';
    empty?.classList.remove('hidden');
    $('pageInfo').textContent = 'No results';
    $('resultsSummary').textContent = '0 leads';
    $('prevPageBtn').disabled = true;
    $('nextPageBtn').disabled = true;
    return;
  }
  empty?.classList.add('hidden');
  tbody.innerHTML = state.leads.map((l) => {
    const sc = STATUS_CFG[l.status] || STATUS_CFG.new;
    const od = isOverdue(l);
    const dueBal = leadOutstandingBalance(l);
    const pkg = l.type === 'ticket' ? formatTicketRoute(l) : esc(l.package_name || '—');
    const quoteCell = fmtAmt(l.type === 'ticket' ? (ticketQuotedTotal(l) ?? l.amount) : l.amount)
      + (dueBal ? `<div class="balance-due-tag">Due ${fmtAmt(dueBal)}</div>` : '');
    const expired = isBookingExpired(l);
    const pool = isPoolLead(l);
    const stalePool = isStalePoolLead(l);
    const priority = expired ? 'row-booking-expired' : stalePool ? 'row-pool-stale' : pool ? 'row-pool-unassigned' : od ? 'row-overdue' : dueBal ? 'row-balance-due' : '';
    const agentLabel = pool
      ? `<span class="agent-chip pool-chip${stalePool ? ' pool-chip-stale' : ''}">Unassigned pool${stalePool ? ' · waiting' : ''}</span>`
      : `<span class="agent-chip"><span class="agent-dot" style="background:${esc(l.agent_color || '#888')}"></span>${esc(l.agent_name || '—')}</span>`;
    const terminalCell = state.assistantEmbed
      ? `<td class="leads-terminal-cell"><button type="button" class="btn btn-primary btn-sm leads-open-terminal" data-lead-id="${esc(l.id)}">Open in terminal</button></td>`
      : '';
    return `<tr data-id="${esc(l.id)}" class="${priority}">
      <td><div class="client-cell">
        <div class="avatar" style="background:${esc(getAvatarColor(l.name))}">${esc(getInitials(l.name))}</div>
        <div><div class="client-name">${esc(l.name)}${l.is_custom ? '<span class="custom-star">★</span>' : ''}</div>
        <div class="client-phone">${esc(l.phone)}</div></div>
      </div></td>
      <td><span class="type-tag"><span class="type-chip ${esc(l.type)}">${esc(l.type)}</span> ${pkg}</span></td>
      <td>${agentLabel}</td>
      <td><span class="badge ${sc.cls}"><span class="badge-dot"></span>${sc.label}${expired ? '<span class="booking-expired-tag">Expired</span>' : ''}</span></td>
      <td class="${od ? 'overdue overdue-pulse' : ''}">${fmtDate(l.followup_date)}</td>
      <td class="${dueBal ? 'quote-cell-due' : ''}">${quoteCell}</td>
      <td class="update-cell">
        <div class="update-by">${esc(l.last_edited_by_name || '—')}</div>
        <div class="update-when">${relativeTime(l.updated_at)}</div>
      </td>
      ${terminalCell}
    </tr>`;
  }).join('');

  $('pageInfo').textContent = `Page ${state.page} of ${state.total_pages}`;
  $('resultsSummary').textContent = `${state.total} lead${state.total === 1 ? '' : 's'} · showing ${state.leads.length}`;
  $('prevPageBtn').disabled = state.page <= 1;
  $('nextPageBtn').disabled = state.page >= state.total_pages;
}

// Last list response for this tab, keyed by its exact query string. Lets a
// reload or back-navigation paint rows instantly while the live fetch runs.
const LIST_SNAPSHOT_KEY = 'mysky_leads_snapshot';
const LIST_SNAPSHOT_TTL_MS = 10 * 60 * 1000;

function readListSnapshot(paramsStr) {
  try {
    const snap = JSON.parse(sessionStorage.getItem(LIST_SNAPSHOT_KEY) || 'null');
    if (snap && snap.params === paramsStr && Date.now() - snap.at < LIST_SNAPSHOT_TTL_MS) return snap.list;
  } catch { /* ignore */ }
  return null;
}

function writeListSnapshot(paramsStr, list) {
  try {
    sessionStorage.setItem(LIST_SNAPSHOT_KEY, JSON.stringify({ params: paramsStr, at: Date.now(), list }));
  } catch { /* quota / private mode */ }
}

function applyListResult(list) {
  state.leads = list.leads || [];
  state.page = list.page;
  state.total_pages = list.total_pages;
  state.total = list.total;
  syncDetailHostLeads(state.leads);
  renderTable();
  renderPoolBanner();
  renderFilterChips(state.filter, state.agents, onFilterChange);
}

let listPaintedOnce = false;

async function refreshList() {
  const gen = ++state.listFetchGen;
  const paramsStr = buildListParams().toString();
  // Scope the snapshot to the signed-in agent so a shared tab never shows
  // another user's rows.
  const snapKey = `${state.agent?.id || ''}|${paramsStr}`;
  if (!listPaintedOnce) {
    const snap = readListSnapshot(snapKey);
    if (snap) {
      applyListResult(snap);
      $('lastSynced').textContent = 'Refreshing…';
    }
  }
  setListLoading(true);
  try {
    const list = await api('/api/leads?' + paramsStr);
    if (gen !== state.listFetchGen) return;
    listPaintedOnce = true;
    applyListResult(list);
    writeListSnapshot(snapKey, list);
    $('lastSynced').textContent = `Synced ${new Date().toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}`;
  } catch (e) {
    if (gen !== state.listFetchGen) return;
    showToast(e.message || 'Failed to load leads', 'error');
  } finally {
    if (gen === state.listFetchGen) setListLoading(false);
  }
}

async function refreshStats() {
  if (!canViewOwner(state.agent)) {
    $('insightOverview')?.classList.add('hidden');
    $('insightProgress')?.classList.add('hidden');
    $('insightAgents')?.classList.add('hidden');
    document.querySelector('[data-leads-tab="overview"]')?.classList.add('hidden');
    document.querySelector('[data-leads-tab="progress"]')?.classList.add('hidden');
    document.querySelector('[data-leads-tab="agents"]')?.classList.add('hidden');
    renderTypePanel();
    renderAgentsPanel();
    return;
  }
  try {
    const params = new URLSearchParams();
    params.set('dept_scope', effectiveDeptScope());
    state.stats = await api('/api/stats?' + params);
    renderOverviewPanel();
    renderProgressPanel();
    renderTypePanel();
    renderAgentsPanel();
  } catch {
    state.stats = null;
    renderTypePanel();
    renderAgentsPanel();
  }
}

function renderOverviewPanel() {
  const s = state.stats;
  if (!s) return;
  $('stTotal').textContent = s.total;
  $('stOverdue').textContent = s.overdue;
  if ($('stBookingExpired')) $('stBookingExpired').textContent = s.booking_expired ?? 0;
  $('stActive').textContent = s.by_bucket?.active || 0;
  $('stCompleted').textContent = s.completed || s.converted || 0;
}

function renderProgressPanel() {
  const s = state.stats;
  if (!s) return;
  const bar = $('pipelineBar');
  const legend = $('pipelineLegend');
  const total = s.total || 0;
  const buckets = s.by_bucket || {};
  const order = ['active', 'waiting_client', 'waiting_supplier', 'completed', 'closed'];
  bar.innerHTML = order.map((key) => {
    const c = buckets[key] || 0;
    if (!c || !total) return '';
    const pct = Math.max(2, (c / total) * 100);
    const label = STATUS_BUCKETS[key]?.label || key;
    return `<button type="button" class="pipeline-seg" data-bucket="${esc(key)}" style="width:${pct}%;background:${PIPE_COLORS[key]}" title="${esc(label)}: ${c}"></button>`;
  }).join('');
  bar.querySelectorAll('[data-bucket]').forEach((btn) => {
    btn.addEventListener('click', () => applyStatFilter(`bucket:${btn.dataset.bucket}`));
  });
  if (legend) {
    legend.textContent = order.filter((k) => buckets[k]).map((k) => `${STATUS_BUCKETS[k]?.label || k} ${buckets[k]}`).join(' · ') || '—';
  }
  $('bucketGrid').innerHTML = order.map((key) => {
    const c = buckets[key] || 0;
    if (!c) return '';
    return `<button type="button" class="leads-bucket-btn" data-stat-filter="bucket:${key}">
      <span>${esc(STATUS_BUCKETS[key]?.label || key)}</span><strong>${c}</strong></button>`;
  }).join('');
}

function renderAgentsPanel() {
  const grid = $('agentGrid');
  if (!grid) return;

  if (!canPickAgentFilter()) {
    grid.innerHTML = [
      `<button type="button" class="leads-filter-card leads-agent-card leads-expired-card${state.filter.booking_expired ? ' active' : ''}" data-stat-filter="booking_expired">
        <span class="leads-filter-card-label">My expired bookings</span><strong>${state.stats?.booking_expired ?? 0}</strong>
      </button>`,
      `<button type="button" class="leads-filter-card leads-agent-card${state.filter.unassigned ? ' active' : ''}" data-unassigned-pool="1">
        <span class="leads-filter-card-label">Unassigned pool</span><strong>New ticket</strong>
      </button>`,
      '<p class="leads-insight-hint">Your assigned inquiries · open Unassigned pool for new ticket intakes.</p>',
    ].join('');
    return;
  }

  const byAgent = state.stats?.by_agent || [];
  const rows = byAgent.length
    ? byAgent
    : visibleAgentsForFilter().map((a) => ({ id: a.id, name: a.name, color: a.color, c: 0 }));

  if (!rows.length) {
    grid.innerHTML = '<p class="leads-insight-hint">No agents found.</p>';
    return;
  }

  const allActive = !state.filter.agent_id && !state.filter.unassigned;
  grid.innerHTML = [
    `<button type="button" class="leads-filter-card leads-agent-card leads-expired-card${state.filter.booking_expired && !state.filter.agent_id ? ' active' : ''}" data-stat-filter="booking_expired">
      <span class="leads-filter-card-label">Expired bookings</span><strong>${state.stats?.booking_expired ?? 0}</strong>
    </button>`,
    `<button type="button" class="leads-filter-card leads-agent-card${state.filter.unassigned ? ' active' : ''}" data-unassigned-pool="1">
      <span class="leads-filter-card-label">Unassigned pool</span><strong>Ticket · New</strong>
    </button>`,
    `<button type="button" class="leads-filter-card leads-agent-card${allActive ? ' active' : ''}" data-agent-id="">
      <span class="leads-filter-card-label">All agents</span>${state.stats?.total != null ? `<strong>${state.stats.total}</strong>` : ''}
    </button>`,
    ...rows.map((a) => {
      const active = state.filter.agent_id === a.id;
      const expiredN = a.expired_c ?? 0;
      return `<button type="button" class="leads-filter-card leads-agent-card${active ? ' active' : ''}" data-agent-id="${esc(a.id)}">
        <span class="agent-dot" style="background:${esc(a.color || '#888')}"></span>
        <span class="leads-agent-name">${esc(a.name)}</span>
        <strong>${a.c ?? 0}${expiredN ? `<span class="agent-expired-count">${expiredN} exp</span>` : ''}</strong>
      </button>`;
    }),
  ].join('');
}

async function loadMeta() {
  const [agentsRes, settingsRes] = await Promise.all([
    api('/api/agents'),
    api('/api/settings'),
  ]);
  state.agents = agentsRes.agents || [];
  state.settings = settingsRes.settings || { sources: [] };
  if (state.settings.status_pipelines) applyPipelineSettings(state.settings.status_pipelines);
  populateStatusSelect();
  populateSourceSelect();
  renderTypePanel();
  renderAgentsPanel();
  syncFilterPanels();
}

function bindEvents() {
  initDensityControls();
  $('logoutBtn')?.addEventListener('click', async () => {
    try { await api('/api/logout', { method: 'POST' }); } catch { /* ignore */ }
    try { sessionStorage.removeItem(LIST_SNAPSHOT_KEY); } catch { /* ignore */ }
    clearSessionHint();
    location.href = '/';
  });
  $('clearFiltersBtn')?.addEventListener('click', clearFilters);
  $('themeToggle')?.addEventListener('click', toggleTheme);
  $('prevPageBtn')?.addEventListener('click', () => {
    if (state.page > 1) { state.page--; writeUrlParams(); refreshList(); }
  });
  $('nextPageBtn')?.addEventListener('click', () => {
    if (state.page < state.total_pages) { state.page++; writeUrlParams(); refreshList(); }
  });

  $('leadsSearch')?.addEventListener('input', debounce((e) => {
    state.filter.q = e.target.value.trim();
    onFilterChange();
  }, 300));

  $('clearFiltersBtn')?.addEventListener('click', clearFilters);

  $('leadsPoolBannerBtn')?.addEventListener('click', () => {
    setActiveTab('browse', false);
    setUnassignedFilter(true);
  });

  $('typeGrid')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-type-filter]');
    if (!btn) return;
    const raw = btn.dataset.typeFilter || 'all';
    setTypeFilter(raw === 'all' ? '' : raw);
  });

  $('agentGrid')?.addEventListener('click', (e) => {
    const statBtn = e.target.closest('[data-stat-filter]');
    if (statBtn) {
      applyStatFilter(statBtn.dataset.statFilter);
      return;
    }
    const poolBtn = e.target.closest('[data-unassigned-pool]');
    if (poolBtn) {
      setUnassignedFilter(!state.filter.unassigned);
      return;
    }
    const btn = e.target.closest('[data-agent-id]');
    if (!btn) return;
    state.filter.unassigned = false;
    setAgentFilter(btn.dataset.agentId || '');
  });

  ['fStatus', 'fSource', 'fSort'].forEach((id) => {
    $(id)?.addEventListener('change', (e) => {
      if (id === 'fSort') {
        state.sort = e.target.value;
      } else if (id === 'fStatus') {
        state.filter.status = e.target.value;
      } else if (id === 'fSource') {
        state.filter.source = e.target.value;
      }
      onFilterChange();
    });
  });

  $('fOverdue')?.addEventListener('click', () => {
    state.filter.overdue = !state.filter.overdue;
    state.filter.focus = false;
    onFilterChange();
  });
  $('fCustom')?.addEventListener('click', () => {
    state.filter.custom = !state.filter.custom;
    onFilterChange();
  });
  $('fBookingExpired')?.addEventListener('click', () => {
    state.filter.booking_expired = !state.filter.booking_expired;
    if (state.filter.booking_expired) {
      state.filter.type = 'ticket';
      state.filter.status = 'temporary_booking';
      state.filter.overdue = false;
      state.filter.focus = false;
    }
    onFilterChange();
  });
  $('deptScopeMy')?.addEventListener('click', () => {
    state.filter.dept_scope = 'my';
    syncDeptScopeUI();
    onFilterChange();
    refreshStats();
  });
  $('deptScopeAll')?.addEventListener('click', () => {
    state.filter.dept_scope = 'all';
    syncDeptScopeUI();
    onFilterChange();
    refreshStats();
  });

  document.querySelectorAll('[data-leads-tab]').forEach((btn) => {
    btn.addEventListener('click', () => setActiveTab(btn.dataset.leadsTab));
  });

  document.querySelectorAll('[data-quick]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.filter.focus = btn.dataset.quick === 'balance' || btn.dataset.quick === 'today';
      state.filter.overdue = btn.dataset.quick === 'overdue';
      state.filter.booking_expired = btn.dataset.quick === 'booking_expired';
      state.filter.bucket = '';
      state.filter.type = btn.dataset.quick === 'booking_expired' ? 'ticket' : '';
      state.filter.status = btn.dataset.quick === 'booking_expired' ? 'temporary_booking' : '';
      if (btn.dataset.quick === 'booking_expired') {
        state.filter.overdue = false;
        state.filter.focus = false;
      }
      if (btn.dataset.quick === 'today') {
        state.filter.overdue = false;
        state.filter.focus = true;
      }
      if (btn.dataset.quick === 'balance') {
        state.filter.overdue = false;
        state.filter.focus = true;
      }
      onFilterChange();
    });
  });

  $('overviewStats')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-stat-filter]');
    if (btn) applyStatFilter(btn.dataset.statFilter);
  });
  $('bucketGrid')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-stat-filter]');
    if (btn) applyStatFilter(btn.dataset.statFilter);
  });

  $('leadsBody')?.addEventListener('click', (e) => {
    const termBtn = e.target.closest('.leads-open-terminal');
    if (termBtn) {
      e.preventDefault();
      e.stopPropagation();
      notifyAssistantOpenLead(termBtn.dataset.leadId);
      return;
    }
    const row = e.target.closest('tr[data-id]');
    if (row) openDetail(row.dataset.id);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === '/' && !e.metaKey && !e.ctrlKey && document.activeElement?.tagName !== 'INPUT') {
      e.preventDefault();
      $('leadsSearch')?.focus();
    }
  });
}

async function boot() {
  installSessionHandler({ redirect: '/' });
  readUrlParams();
  applyTabUi();
  bindEvents();

  try {
    const me = await api('/api/me');
    state.agent = me.agent;
    if (state.agent.must_change_password) {
      location.href = '/';
      return;
    }
    if (isViewer(state.agent)) {
      location.href = '/owner/';
      return;
    }
    $('agentName').textContent = state.agent.name;
    $('agentDot').style.background = state.agent.color || '#00AEEF';
    initPageChrome(state.agent);
    applyNavAccess(state.agent);
    applyAssistantEmbedUi();
    syncDeptScopeUI();
    syncToolbarFromFilter();
    setActiveTab(state.tab in { focus: 1, overview: 1, progress: 1, type: 1, agents: 1, browse: 1 } ? state.tab : DEFAULT_TAB, false);
    if (!canViewOwner(state.agent) && ['overview', 'progress', 'agents'].includes(state.tab)) {
      setActiveTab(DEFAULT_TAB, false);
    }

    // Fire every independent request at once; the table paints as soon as
    // the list returns instead of waiting behind shells, meta and stats.
    const listP = refreshList();
    const statsP = refreshStats();
    const poolP = refreshPoolCount();
    await Promise.all([loadDetailShell(), loadMeta()]);
    await initDetailHost(state, { refreshList, refreshStats, renderList: renderTable });
    loadAccessInbox();
    await Promise.all([listP, statsP]);
    renderFilterChips(state.filter, state.agents, onFilterChange);
    await poolP;
  } catch {
    location.href = '/';
  }
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}

boot();
